import { Component, EventEmitter, Input, Output } from '@angular/core';
import { LegendPosition } from '@swimlane/ngx-charts';

interface PolarChartSeries {
  name: string;
  series: any[];
}

@Component({
    selector: 'app-conv-polar-chart',
    templateUrl: './conv-polar-chart.component.html',
    styleUrl: './conv-polar-chart.component.scss',
    standalone: false
})
export class ConvPolarChartComponent {
	@Input() rgbData: any[] = [];
	@Input() blinksData: any[] = [];
	@Input() heatmap_leaks: any[] = [];
	@Input() polar_intensity: any[] = [];
	@Input() polar_pulse: any[] = [];
	@Input() heatmap_leaks_max_value = 0;
	@Output() reloadData: EventEmitter<any> = new EventEmitter<any>();
	
	// options
	view: [number, number] = [500, 250];
  legend = true;
  animations = false;
  xAxis = true;
  yAxis = true;
  showYAxisLabel = true;
  showXAxisLabel = true;
	legendPosition = LegendPosition.Right
	min = 0;
	max = 100;

	red = '#FF0000'; // 0
	yellow = '#FFFF00'; // 1
	green = '#008000'; // 2
	aqua = '#00FFFF'; // 3
	purple = '#800080'; // 4
	pink = '#FF1493'; // 5
	brown = '#A66500'; // 6

	colorScheme: any = {
    domain: [
			this.red, 
			this.yellow, 
			this.green, 
			this.aqua, 
			this.purple, 
			this.pink, 
			this.brown
		]
  };

	window_time = 3;
	chart_type = 'def';
	lastCurrectTime = 0;

	windowTimes = [
		{ name: '3 Seconds', value: 3 },
		{ name: '5 Seconds', value: 5 },
		{ name: '10 Seconds', value: 10 },
	];

	chartTypes = [
		{ name: 'Default', value: 'def' },
		{ name: 'Intensity & Pulse', value: 'inp' },
	];

	polarChartData: PolarChartSeries[] = [
		{ name: "Cognitive", series: [] },
		{ name: "Emotion", series: [] },
		{ name: "Engagment", series: [] },
		{ name: "Left Brow", series: [] }, // lbe
		{ name: "Right Brow", series: [] }, // rbe
		{ name: "Left Eye", series: [] }, // lee
		{ name: "Right Eye", series: [] } // ree
	]

	polarChartIntensity: PolarChartSeries[] = [
		{ name: "Intensity", series: [] },
		{ name: "Pulse", series: [] }
	]

	chartData: any[] = [];

	ready = false;

	last_cog = 0;
	last_emo = 0;
	last_blinks = 0;
	
	last_lbe = 0;
	last_rbe = 0;
	last_lee = 0;
	last_ree = 0;
	
	last_intensity = 0;
	last_pulse = 0;

	showChart() {
		this.ready = true;
		this.calculateChartData(this.window_time);
	}

	calculateChartData(currentTime: number) {
		if (currentTime >= this.window_time) {
			if (currentTime !== this.lastCurrectTime) {
				if (this.chart_type === 'def') {
					if (currentTime >= this.window_time) {
						this.polarChartData.forEach(e => {
							e.series = [];
						});
		
						for (let index = currentTime; index > currentTime - this.window_time; index--) {
							const time = this.convertToTimerFormat(index)
				
							if (this.rgbData.length > 0) {
								if (index < this.rgbData.length) {
									this.polarChartData[0].series.push({ name: time, value: +(this.rgbData[index].cognitive) });
									this.polarChartData[1].series.push({ name: time, value: +(this.rgbData[index].emotional) });
									this.last_cog = +(this.rgbData[index].cognitive);
									this.last_emo = +(this.rgbData[index].emotional);
								} else {
									this.polarChartData[0].series.push({ name: time, value: this.last_cog });
									this.polarChartData[1].series.push({ name: time, value: this.last_emo });
								}
							} else {
								this.polarChartData[0].series.push({ name: time, value: 0 });
								this.polarChartData[1].series.push({ name: time, value: 0 });
							}
				
							if (this.blinksData.length > 0) {
								if (index < this.blinksData.length) {
									this.polarChartData[2].series.push({ name: time, value: +this.blinksData[index].blinks.toFixed(2) });
									this.last_blinks = +this.blinksData[index].blinks.toFixed(2);
								} else {
									this.polarChartData[2].series.push({ name: time, value: this.last_blinks });
								}
							} else {
								this.polarChartData[2].series.push({ name: time, value: 0 });
							}
				
							if (this.heatmap_leaks.length > 0) {
								if (index < this.heatmap_leaks[0].length) {
									this.polarChartData[3].series.push({ name: time, value: this.calculateLeaksValue(this.heatmap_leaks[2][index]) }) // lbe
									this.polarChartData[4].series.push({ name: time, value: this.calculateLeaksValue(this.heatmap_leaks[3][index]) }) // rbe
									this.polarChartData[5].series.push({ name: time, value: this.calculateLeaksValue(this.heatmap_leaks[6][index]) }) // lee
									this.polarChartData[6].series.push({ name: time, value: this.calculateLeaksValue(this.heatmap_leaks[7][index]) }) // ree
	
									this.last_lbe = this.calculateLeaksValue(this.heatmap_leaks[2][index]);
									this.last_rbe = this.calculateLeaksValue(this.heatmap_leaks[3][index]);
									this.last_lee = this.calculateLeaksValue(this.heatmap_leaks[6][index]);
									this.last_ree = this.calculateLeaksValue(this.heatmap_leaks[7][index]);
								} else {
									this.polarChartData[3].series.push({ name: time, value: this.last_lbe });
									this.polarChartData[4].series.push({ name: time, value: this.last_rbe });
									this.polarChartData[5].series.push({ name: time, value: this.last_lee });
									this.polarChartData[6].series.push({ name: time, value: this.last_ree });
								}
							} else {
								this.polarChartData[3].series.push({ name: time, value: 0 });
							}
						}
					} else {
						this.polarChartData.forEach(e => {
							e.series = [];
						});
					}
				} else {
					if (currentTime >= this.window_time) {
						this.polarChartIntensity.forEach(e => {
							e.series = [];
						});
		
						for (let index = currentTime; index > currentTime - this.window_time; index--) {
							const time = this.convertToTimerFormat(index)
				
							if (this.polar_intensity.length > 0) {
								if (index < this.polar_intensity.length && +(this.polar_intensity[index]) > 0) {
									this.polarChartIntensity[0].series.push({ name: time, value: +(this.polar_intensity[index]) });
									this.last_intensity = +(this.polar_intensity[index]);
								} else {
									this.polarChartIntensity[0].series.push({ name: time, value: this.last_intensity });
								}
							} else {
								this.polarChartIntensity[0].series.push({ name: time, value: 0 });
							}
				
							if (this.polar_pulse.length > 0) {
								if (index < this.polar_pulse.length && +(this.polar_pulse[index]) > 0) {
									this.polarChartIntensity[1].series.push({ name: time, value: +(this.polar_pulse[index]) });
									this.last_pulse = +(this.polar_pulse[index]);
								} else {
									this.polarChartIntensity[1].series.push({ name: time, value: this.last_pulse });
								}
							} else {
								this.polarChartIntensity[1].series.push({ name: time, value: 0 });
							}
				
						}
					} else {
						this.polarChartData.forEach(e => {
							e.series = [];
						});
					}

				}
		
				if (this.chart_type === 'def') {
					this.chartData = [...this.polarChartData];
				} else {
					this.chartData = [...this.polarChartIntensity];
				}
				this.lastCurrectTime = currentTime;		
			}
	
			if (!this.ready) {
				this.ready = true;
			}
		}
	}

	changeWindowTime() {
		this.reloadData.emit();
	}

	updateChartWindowTime(currentTime: number) {
		if (currentTime < this.window_time) {
			currentTime = this.window_time;
		}

		if (this.chart_type === 'def') {
			this.polarChartData.forEach(e => {
				e.series = [];
			});
	
			for (let index = currentTime; index > currentTime - this.window_time; index--) {
				const time = this.convertToTimerFormat(index)
	
				if (this.rgbData.length > 0) {
					if (index < this.rgbData.length) {
						this.polarChartData[0].series.push({ name: time, value: +(this.rgbData[index].cognitive) });
						this.polarChartData[1].series.push({ name: time, value: +(this.rgbData[index].emotional) });
						this.last_cog = +(this.rgbData[index].cognitive);
						this.last_emo = +(this.rgbData[index].emotional);
					} else {
						this.polarChartData[0].series.push({ name: time, value: this.last_cog });
						this.polarChartData[1].series.push({ name: time, value: this.last_emo });
					}
				} else {
					this.polarChartData[0].series.push({ name: time, value: 0 });
					this.polarChartData[1].series.push({ name: time, value: 0 });
				}
	
				if (this.blinksData.length > 0) {
					if (index < this.blinksData.length) {
						this.polarChartData[2].series.push({ name: time, value: +this.blinksData[index].blinks.toFixed(2) });
						this.last_blinks = +this.blinksData[index].blinks.toFixed(2);
					} else {
						this.polarChartData[2].series.push({ name: time, value: this.last_blinks });
					}
				} else {
					this.polarChartData[2].series.push({ name: time, value: 0 });
				}
	
				if (this.heatmap_leaks.length > 0) {
					if (index < this.heatmap_leaks[0].length) {
						this.polarChartData[3].series.push({ name: time, value: this.calculateLeaksValue(this.heatmap_leaks[2][index]) }) // lbe
						this.polarChartData[4].series.push({ name: time, value: this.calculateLeaksValue(this.heatmap_leaks[3][index]) }) // rbe
						this.polarChartData[5].series.push({ name: time, value: this.calculateLeaksValue(this.heatmap_leaks[6][index]) }) // lee
						this.polarChartData[6].series.push({ name: time, value: this.calculateLeaksValue(this.heatmap_leaks[7][index]) }) // ree
	
						this.last_lbe = this.calculateLeaksValue(this.heatmap_leaks[2][index]);
						this.last_rbe = this.calculateLeaksValue(this.heatmap_leaks[3][index]);
						this.last_lee = this.calculateLeaksValue(this.heatmap_leaks[6][index]);
						this.last_ree = this.calculateLeaksValue(this.heatmap_leaks[7][index]);
					} else {
						this.polarChartData[3].series.push({ name: time, value: this.last_lbe });
						this.polarChartData[4].series.push({ name: time, value: this.last_rbe });
						this.polarChartData[5].series.push({ name: time, value: this.last_lee });
						this.polarChartData[6].series.push({ name: time, value: this.last_ree });
					}
				} else {
					this.polarChartData[3].series.push({ name: time, value: 0 });
				}
			}
	
			this.chartData = [...this.polarChartData];
		} else {
			this.polarChartIntensity.forEach(e => {
				e.series = [];
			});

			for (let index = currentTime; index > currentTime - this.window_time; index--) {
				const time = this.convertToTimerFormat(index)
	
				if (this.polar_intensity.length > 0) {
					if (index < this.polar_intensity.length && +(this.polar_intensity[index]) > 0) {
						this.polarChartIntensity[0].series.push({ name: time, value: +(this.polar_intensity[index]) });
						this.last_intensity = +(this.polar_intensity[index]);
					} else {
						this.polarChartIntensity[0].series.push({ name: time, value: this.last_intensity });
					}
				} else {
					this.polarChartIntensity[0].series.push({ name: time, value: 0 });
				}
	
				if (this.polar_pulse.length > 0) {
					if (index < this.polar_pulse.length && +(this.polar_pulse[index]) > 0) {
						this.polarChartIntensity[1].series.push({ name: time, value: +(this.polar_pulse[index]) });
						this.last_pulse = +(this.polar_pulse[index]);
					} else {
						this.polarChartIntensity[1].series.push({ name: time, value: this.last_pulse });
					}
				} else {
					this.polarChartIntensity[1].series.push({ name: time, value: 0 });
				}
			}

			this.chartData = [...this.polarChartIntensity];
		}

		this.lastCurrectTime = currentTime;		

		if (!this.ready) {
			this.ready = true;
		}
	}

	tooltipText(data: any): string {
		const cell = JSON.parse(JSON.stringify(data)).cell;

		let spanContent;

		if (Array.isArray(cell.original)) {
			const listItems = cell.original.map((item: any) => `<li>${item}</li>`).join('');
			spanContent = `<ol>${listItems}</ol>`;
		} else {
			spanContent = cell.original;
		}

		const span = `<span>${spanContent}</span>`;

		const tool = `
			<div class="p-1 fs-16">
				${cell.series} - ${cell.name}
				<hr class="m-0">
				${span}
			</div>
		`;

		return tool;
	}

	updateChart(currentTime: number) {
		this.calculateChartData(currentTime);
	}

	calculateLeaksValue(value: number) {
		return (+((value / this.heatmap_leaks_max_value) * 100).toFixed(2));
	}

	convertToTimerFormat(seconds: number) {
		if (seconds < 3600) {
			// Display "mm:ss" format for the first 60 minutes
			const minutes = Math.floor(seconds / 60);
			const formattedSeconds = (seconds % 60).toString().padStart(2, "0");
			const formattedMinutes = minutes.toString().padStart(2, "0");
			return `${formattedMinutes}:${formattedSeconds}`;
		} else {
			// After 60 minutes, switch to "hh:mm:ss" format
			const hours = Math.floor(seconds / 3600);
			const remainingSeconds = seconds % 3600;
			const minutes = Math.floor(remainingSeconds / 60);
			const formattedSeconds = (remainingSeconds % 60).toString().padStart(2, "0");
			const formattedMinutes = minutes.toString().padStart(2, "0");
			const formattedHours = hours.toString().padStart(2, "0");
			return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
		}
	}
}
